import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import type { AssetOpportunities, AssetOpportunityRow } from '../../store/insights/insights'
import { fmt, type SortDir } from './insightsUtils'
import { InsightsSection, SortIcon, NumericTrendBadge } from './insightsCommon'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'

type AssetOppSortKey = 'meetingType' | 'assetMentions' | 'suggestionMade' | 'clientAgreed'

const AssetOppSortableHeader: React.FC<{
  label: string
  sortKey: AssetOppSortKey
  activeKey: AssetOppSortKey
  activeDir: SortDir
  onSort: (key: AssetOppSortKey) => void
  isName?: boolean
}> = ({ label, sortKey, activeKey, activeDir, onSort, isName }) => {
  const isActive = sortKey === activeKey
  const ariaSort = isActive ? (activeDir === 'asc' ? 'ascending' : 'descending') : 'none'
  const sortDirectionLabel = isActive ? (activeDir === 'asc' ? 'sorted ascending' : 'sorted descending') : 'not sorted'

  return (
    <button
      type='button'
      className={classNames('insights-asset-opp__table-cell', 'insights-sortable-th', {
        'insights-asset-opp__table-cell--name': isName,
      })}
      onClick={() => onSort(sortKey)}
      role='columnheader'
      aria-sort={ariaSort}
      aria-label={`${label}, ${sortDirectionLabel}`}
    >
      <div className='insights-sortable-th-inner'>
        <span>{label}</span>
        {isActive && <SortIcon dir={activeDir} />}
      </div>
    </button>
  )
}

const AssetOppTable: React.FC<{ rows: AssetOpportunityRow[] }> = ({ rows }) => {
  const [sortKey, setSortKey] = useState<AssetOppSortKey>('assetMentions')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const { trackingClient } = useTracking()

  const onSort = (key: AssetOppSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'meetingType' ? 'asc' : 'desc')
    }
    trackingClient.track({ eventName: TrackEvent.INSIGHTS_SORT_CHANGED, eventDetails: { section: 'assetOpportunities', column: key } })
  }

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      let cmp: number
      if (sortKey === 'meetingType') {
        cmp = a.meetingType.localeCompare(b.meetingType)
      } else {
        cmp = a[sortKey] - b[sortKey]
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [rows, sortKey, sortDir])

  return (
    <div className='insights-asset-opp__table'>
      <div className='insights-asset-opp__table-header'>
        <AssetOppSortableHeader label='Meeting type' sortKey='meetingType' activeKey={sortKey} activeDir={sortDir} onSort={onSort} isName />
        <AssetOppSortableHeader label='Asset mentions' sortKey='assetMentions' activeKey={sortKey} activeDir={sortDir} onSort={onSort} />
        <AssetOppSortableHeader label='Suggestions made' sortKey='suggestionMade' activeKey={sortKey} activeDir={sortDir} onSort={onSort} />
        <AssetOppSortableHeader label='Client agreed' sortKey='clientAgreed' activeKey={sortKey} activeDir={sortDir} onSort={onSort} />
      </div>
      {sortedRows.map((row, i) => (
        <div
          key={row.meetingType}
          className={classNames('insights-asset-opp__table-row', { 'insights-asset-opp__table-row--last': i === sortedRows.length - 1 })}
        >
          <div className='insights-asset-opp__table-cell insights-asset-opp__table-cell--name'>{row.meetingType}</div>
          <div className='insights-asset-opp__table-cell'>
            <span className='fw-medium'>{fmt(row.assetMentions)}</span>
          </div>
          <div className='insights-asset-opp__table-cell'>
            <span className='fw-medium'>
              {fmt(row.suggestionMade)} <span className='text-muted'>({row.suggestionMadePct}%)</span>
            </span>
          </div>
          <div className='insights-asset-opp__table-cell'>
            <span className='fw-medium'>
              {fmt(row.clientAgreed)} <span className='text-muted'>({row.clientAgreedPct}%)</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  )
}

export const AssetOpportunitiesSection: React.FC<{ data: AssetOpportunities }> = ({ data }) => {
  const { metrics, rows } = data

  return (
    <InsightsSection title='Asset opportunities' subtitle='Track how held-away assets move from mention to client agreement'>
      <div className='insights-asset-opp__metrics'>
        <div className='insights-asset-opp__metric'>
          <div className='insights-asset-opp__metric-bar insights-asset-opp__metric-bar--1' />
          <div className='insights-asset-opp__metric-content'>
            <div className='insights-asset-opp__metric-label'>Asset mentions</div>
            <div className='insights-asset-opp__metric-value-row'>
              <span className='insights-asset-opp__metric-value'>{fmt(metrics.assetMentions)}</span>
              <NumericTrendBadge value={metrics.assetMentionsTrend} />
            </div>
            <div className='insights-asset-opp__metric-detail'>
              Came up most in <strong>{metrics.assetMentionsLeadMeetingType}</strong>
            </div>
          </div>
        </div>

        <div className='insights-asset-opp__metric'>
          <div className='insights-asset-opp__metric-bar insights-asset-opp__metric-bar--2' />
          <div className='insights-asset-opp__metric-content'>
            <div className='insights-asset-opp__metric-label'>Suggestions made</div>
            <div className='insights-asset-opp__metric-value-row'>
              <span className='insights-asset-opp__metric-value'>{fmt(metrics.suggestionsMade)}</span>
              <NumericTrendBadge value={metrics.suggestionsMadeTrend} />
            </div>
            <div className='insights-asset-opp__metric-detail'>
              Most suggestions in <strong>{metrics.suggestionsLeadMeetingType}</strong>
            </div>
          </div>
        </div>

        <div className='insights-asset-opp__metric'>
          <div className='insights-asset-opp__metric-bar insights-asset-opp__metric-bar--3' />
          <div className='insights-asset-opp__metric-content'>
            <div className='insights-asset-opp__metric-label'>Client agreed</div>
            <div className='insights-asset-opp__metric-value-row'>
              <span className='insights-asset-opp__metric-value'>{fmt(metrics.clientAgreed)}</span>
              <NumericTrendBadge value={metrics.clientAgreedTrend} />
            </div>
            <div className='insights-asset-opp__metric-detail'>
              Clients agree most in <strong>{metrics.clientAgreedLeadMeetingType}</strong>
            </div>
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <>
          <AssetOppTable rows={rows} />

          <div className='insights-asset-opp__footer'>
            <div className='insights-asset-opp__footer-badge'>
              <i className='bi bi-info-circle' />
              <span>Only meetings with asset mentions are shown</span>
            </div>
          </div>
        </>
      )}
    </InsightsSection>
  )
}
